import React, {ReactElement, ReactNode} from 'react';
import {cx} from '@emotion/css';
import {RequireAtLeastOne} from '@fo/shared-types';
import DynamicLink from '@fo/components/DynamicLink/DynamicLink';
import {BRS_ORIGIN_URL} from '@fo/shared-types/constants';
import {getPageRoute} from '@fo/utils/routes/getPageRoute';

import {isBrowser} from '@fo/shared/utils/browserSupport';
import {ButtonVariant} from './types';

import styles from './Button.styles';

export {ButtonVariant} from './types';

export interface ButtonProps {
  ariaLabel: string;
  blank?: boolean;
  children: ReactNode;
  className?: string;
  forceAnchorLink?: boolean;
  href?: string;
  icon?: ReactNode;
  id?: string;
  isDisabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  testId?: string;
  title?: string;
  type?: React.ComponentProps<'button'>['type'];
  variant: ButtonVariant;
  iconClassName?: string;
  download?: boolean;
}

export type Props = RequireAtLeastOne<ButtonProps, 'onClick' | 'href'>;

function Button(props: Props): ReactElement {
  const {
    ariaLabel,
    blank,
    children,
    className,
    href,
    id,
    isDisabled,
    forceAnchorLink,
    onClick = () => null,
    testId,
    title,
    type,
    variant,
    icon,
    iconClassName,
    download,
  } = props;

  const brsOriginUrl = isBrowser ? window?.sessionStorage.getItem(BRS_ORIGIN_URL) : undefined;

  if (href) {
    return (
      <DynamicLink
        download={download}
        href={
          // if the CTA points to /get-started ...
          href.includes(getPageRoute('quoteForm.start').replace(/\//g, '')) // -> removes /'s from route
            ? // ...use the redirect url stored in the session.
              // In case there is no redirect url stored, use the button's url from contentful
              // no matter where it points to.
              brsOriginUrl ?? href
            : // or, if it's not pointing to /get-started, use Contentful's button url
              href
        }
        className={cx(styles.container(variant, isDisabled), 'button', className)}
        aria-label={ariaLabel}
        title={title}
        testId={testId || id}
        forceAnchorLink={forceAnchorLink}
        blank={blank}
        onClick={onClick}
        isDisabled={isDisabled}
      >
        {icon && <div className={cx(styles.icon, iconClassName)}>{icon}</div>}
        {children}
      </DynamicLink>
    );
  }
  /* eslint-disable react/button-has-type */
  return (
    <button
      className={cx(styles.container(variant, isDisabled), 'button', className)}
      aria-label={ariaLabel}
      title={title}
      data-testid={testId || id}
      disabled={isDisabled}
      id={id}
      onClick={onClick}
      type={type}
    >
      {icon && <div className={cx(styles.icon, iconClassName)}>{icon}</div>}
      {children}
    </button>
  );
}

export default Button;
